/**
 * Moderation Queue — operational-queue spec helpers.
 * Case ID MOD-###### retained into Archive (no separate Archive ID).
 * Source: Customer | Business | System
 * Status in Queue: New | Under Review | Decided (while Archive move unfinished)
 * Types: Post | Review | Business Reply | Message | Quote | Business Profile Item
 *
 * Ownership:
 * - Configuration — Flag owns qualification, Severity, Case Module, case create
 * - Content Control owns content checking; does not create Flags or cases
 * - Moderation Queue owns assignment, review, decisions, actual results
 * - Case Actions govern supported staff submissions on existing cases
 * - Publishing / Messages·Quotes / Notifications own hide, pause, notices
 * - Lifecycle Control moves Decided cases to Moderation Archive
 */

import {
  getArchivedModerationCaseIdSet,
  getQueueCases,
  getScenarioByCaseId,
  getScenarioByFlagId,
} from './postModerationScenarios';
import { getReviewQueueCases, getReviewScenarioByCaseId } from './reviewModerationScenarios';
import { getMessageQueueCases, getMessageScenarioByCaseId } from './messageModerationScenarios';
import { getQuoteQueueCases, getQuoteScenarioByCaseId } from './quoteModerationScenarios';
import { getProfileQueueCases, getProfileScenarioByCaseId } from './profileModerationScenarios';
import { buildSampleFormInventory } from './formInventoryConfig';
import { buildSampleContentControlRules } from './contentControlConfig';
import { urlNameSegment } from '../utils/slugify';
import { applyQueueSession } from '../utils/moderationCaseSession';

export const MODERATION_DECISION_FORM_ID = 'FORM-000040';
export const MODERATION_DECISION_FORM_NAME = 'Moderation Decision Form';
export const MODERATION_DECISION_FORM_DISPLAY = `${MODERATION_DECISION_FORM_ID} — ${MODERATION_DECISION_FORM_NAME}`;

/** Queue / Archive Case ID prefix (same ID retained into Archive). */
export const MODERATION_QUEUE_CASE_PREFIX = 'MOD';
/** @deprecated Legacy Archive prefix — Archive now keeps the Queue Case ID. */
export const MODERATION_ARCHIVE_CASE_PREFIX = 'MOD';

/** Strip MOD- / MOA- / legacy MODS- so lookups match by numeric body. */
export const stripModerationCasePrefix = (caseId) =>
  String(caseId || '').replace(/^(?:MODS|MOA|MOD)-?/i, '');

export const formatModerationQueueCaseId = (caseId) => {
  const raw = String(caseId || '').trim();
  if (!raw || raw === '—') return raw;
  if (/^MOD-DEC-/i.test(raw)) return raw.toUpperCase();
  if (/^(?:MODS|MOA|MOD)-/i.test(raw)) {
    const digits = stripModerationCasePrefix(raw).replace(/\D/g, '');
    if (!digits) return raw.toUpperCase();
    return `${MODERATION_QUEUE_CASE_PREFIX}-${digits.padStart(6, '0')}`;
  }
  const digits = raw.replace(/\D/g, '');
  if (!digits) return raw;
  return `${MODERATION_QUEUE_CASE_PREFIX}-${digits.padStart(6, '0')}`;
};

/** Archive keeps the same Moderation Case ID (no separate Archive ID). */
export const archiveIdFromQueueCaseId = (caseId) => formatModerationQueueCaseId(caseId);

export const QUEUE_CASE_TYPES = [
  'Post',
  'Review',
  'Business Reply',
  'Message',
  'Quote',
  'Business Profile Item',
];

export const QUEUE_STATUSES = ['New', 'Under Review', 'Decided'];

export const QUEUE_SEVERITIES = ['Low', 'Medium', 'High'];

export const QUEUE_SOURCES = ['Customer', 'Business', 'System'];

export const QUEUE_SUPPORT_TEAM = [
  'Alex Chen',
  'John Smith',
  'Sarah Johnson',
  'Mike Davis',
  'Emma Wilson',
];

export const FINDING_OPTIONS = ['Confirmed', 'Not Confirmed', 'Unable to Determine'];

export const DECISION_OUTCOME_OPTIONS_QUEUE = ['Confirmed', 'Not Confirmed'];

export const DEFAULT_DECISION_CHECK_LABELS = [
  'Captured evidence reviewed',
  'Flagged items identified',
  'Decision Reason completed',
  'Decision Form requirements completed',
];

const CONTENT_CONTROL_RULE_BY_TYPE = {
  Post: { id: 'CCT-0002', issue: 'Pricing or price information' },
  Review: { id: 'CCT-0004', issue: 'Explicit or prohibited review image' },
  'Business Reply': { id: 'CCT-0004', issue: 'Prohibited business reply content' },
  'Business Profile Item': { id: 'CCT-0003', issue: 'Impersonation or misrepresentation' },
};

const dash = (value) => {
  if (value == null) return '—';
  const text = String(value).trim();
  return text === '' || text.toLowerCase() === 'null' || text.toLowerCase() === 'undefined'
    ? '—'
    : text;
};

export const formatQueueDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const datePart = date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('en-AU', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${datePart}, ${timePart}`;
};

export const normalizeSupportTeam = (value) => {
  if (!value || String(value).toLowerCase() === 'unassigned') return 'Unassigned';
  const name = String(value)
    .replace(/^Fraud\s*&\s*Security — /i, '')
    .replace(/^(Admin|Compliance|Support|Moderator|Owner|Finance|Development|Team Member) — /i, '')
    .replace(/^Admin: /, '')
    .replace(/\s*\([^)]*\)\s*$/, '')
    .split(' (')[0]
    .trim();
  return name || 'Unassigned';
};

export const normalizeQueueStatus = (status) => {
  const raw = String(status || '').trim();
  if (!raw) return 'New';
  if (raw === 'New') return 'New';
  // Legacy Awaiting Information collapses to Under Review (Unable to Determine is a finding, not a status).
  if (
    raw === 'Awaiting Information' ||
    raw === 'Information Requested' ||
    raw === 'Active' ||
    raw === 'Under Review' ||
    raw === 'In Review' ||
    raw === 'Assigned' ||
    raw === 'Park' ||
    raw === 'Parked'
  ) {
    return 'Under Review';
  }
  if (
    raw === 'Decided' ||
    raw === 'Resolved' ||
    raw === 'Closed' ||
    raw === 'Archived'
  ) {
    return 'Decided';
  }
  return raw;
};

export const isQueueStatus = (status) => {
  const normalized = normalizeQueueStatus(status);
  return (
    normalized === 'New' ||
    normalized === 'Under Review' ||
    normalized === 'Decided'
  );
};

export const normalizeQueueSource = (row = {}, scenario) => {
  const flag = scenario?.flag || {};
  const raw = String(
    row.queueSource ||
      flag.source ||
      row.flagSource ||
      row.caseSource ||
      row.source ||
      ''
  ).toLowerCase();

  if (
    raw === 'system' ||
    raw.includes('system') ||
    raw.includes('detection') ||
    raw.includes('signal')
  ) {
    return 'System';
  }
  if (raw.includes('business')) return 'Business';
  if (raw.includes('customer') || raw.includes('user') || raw.includes('reporter')) {
    return 'Customer';
  }

  if (scenario?.reporter || String(flag.flaggedBy || '').trim()) {
    const flaggedBy = String(flag.flaggedBy || scenario?.reporter?.name || '').toLowerCase();
    const businessName = String(
      scenario?.business?.businessName || scenario?.review?.business?.name || ''
    ).toLowerCase();
    if (businessName && flaggedBy && flaggedBy === businessName) return 'Business';
    if (scenario?.reporter) return 'Customer';
    if (flaggedBy) return 'Customer';
  }

  return 'Customer';
};

export const normalizeQueueType = (row = {}, scenario) => {
  const raw = String(
    row.queueType ||
      row.contentType ||
      row.type ||
      scenario?.case?.contentType ||
      ''
  ).trim();

  if (raw === 'Business Reply' || raw === 'Business Reply') return 'Business Reply';
  if (
    raw === 'Business Profile Item' ||
    raw === 'Business Profile Item'
  ) {
    return 'Business Profile Item';
  }
  if (raw === 'Profile' || raw === 'Business Profile' || raw === 'Business Profiles') {
    if (row.profileType === 'Customer' || scenario?.profileType === 'Customer') return null;
    return 'Business Profile Item';
  }
  if (raw === 'Customer Profile' || raw === 'Customer Profiles') return null;
  if (raw === 'Posts') return 'Post';
  if (raw === 'Reviews') return 'Review';
  if (raw === 'Messages') return 'Message';
  if (raw === 'Direct Quote' || raw === 'Group Quote') return 'Quote';
  if (QUEUE_CASE_TYPES.includes(raw)) return raw;
  if (String(row.reviewTarget || '').includes('Business Reply')) return 'Business Reply';
  if (scenario?.post) return 'Post';
  // Prefer explicit case contentType / linked reply case over "has a reply" on the review.
  if (scenario?.replyCase && String(scenario.replyCase.caseId) === String(row.caseId)) {
    return 'Business Reply';
  }
  if (scenario?.review && !String(raw).includes('Reply')) return 'Review';
  if (scenario?.review?.reply) return 'Business Reply';
  if (scenario?.review) return 'Review';
  if (scenario?.conversation) return 'Message';
  if (scenario?.quote) return 'Quote';
  if (scenario?.profileType === 'Business') return 'Business Profile Item';
  return null;
};

const pickAccountName = (...candidates) => {
  for (const value of candidates) {
    const text = value == null ? '' : String(value).trim();
    if (text && text !== '—') return text;
  }
  return '—';
};

const resolveAccount = (row = {}, type, scenario) => {
  const flaggedMessage = (scenario?.messages || []).find((m) => m.isFlagged);
  const flaggedQuote = (scenario?.chatMessages || []).find((m) => m.isFlagged);
  const rowKind = String(row.accountType || row.accountKind || '').toLowerCase();

  if (type === 'Post' || type === 'Business Profile Item') {
    return {
      accountKind: 'Business',
      accountName: pickAccountName(
        scenario?.business?.businessName,
        row.businessName,
        row.accountName
      ),
      businessId: scenario?.business?.businessId || row.businessId || '—',
      customerId: '—',
    };
  }

  if (type === 'Review') {
    return {
      accountKind: 'Customer',
      accountName: pickAccountName(
        scenario?.review?.reviewer?.name,
        row.customerName,
        row.accountName
      ),
      businessId: scenario?.review?.business?.id || row.businessId || '—',
      customerId: scenario?.review?.reviewer?.id || row.customerId || '—',
    };
  }

  if (type === 'Business Reply') {
    return {
      accountKind: 'Business',
      accountName: pickAccountName(
        scenario?.review?.business?.name,
        scenario?.business?.businessName,
        row.businessName,
        row.accountName
      ),
      businessId: scenario?.review?.business?.id || row.businessId || '—',
      customerId: scenario?.review?.reviewer?.id || row.customerId || '—',
    };
  }

  if (type === 'Message') {
    const sender = String(flaggedMessage?.sender || '').toLowerCase();
    const preferBusiness = sender === 'business' || rowKind === 'business';
    if (preferBusiness) {
      return {
        accountKind: 'Business',
        accountName: pickAccountName(
          scenario?.business?.businessName,
          row.businessName,
          row.accountName
        ),
        businessId: scenario?.business?.businessId || row.businessId || '—',
        customerId: scenario?.customer?.customerId || row.customerId || '—',
      };
    }
    return {
      accountKind: rowKind === 'business' ? 'Business' : 'Customer',
      accountName: pickAccountName(
        scenario?.customer?.name,
        row.customerName,
        row.accountName,
        scenario?.business?.businessName,
        row.businessName
      ),
      businessId: scenario?.business?.businessId || row.businessId || '—',
      customerId: scenario?.customer?.customerId || row.customerId || '—',
    };
  }

  if (type === 'Quote') {
    const sender = String(flaggedQuote?.sender || '').toLowerCase();
    const preferBusiness = sender === 'business' || rowKind === 'business';
    if (preferBusiness) {
      return {
        accountKind: 'Business',
        accountName: pickAccountName(
          scenario?.business?.businessName,
          row.businessName,
          row.accountName
        ),
        businessId: scenario?.business?.businessId || row.businessId || '—',
        customerId: scenario?.customer?.customerId || row.customerId || '—',
      };
    }
    return {
      accountKind: rowKind === 'business' ? 'Business' : 'Customer',
      accountName: pickAccountName(
        scenario?.customer?.name,
        row.customerName,
        row.accountName,
        scenario?.business?.businessName,
        row.businessName
      ),
      businessId: scenario?.business?.businessId || row.businessId || '—',
      customerId: scenario?.customer?.customerId || row.customerId || '—',
    };
  }

  return {
    accountKind: row.accountType === 'Customer' ? 'Customer' : 'Business',
    accountName: pickAccountName(row.accountName, row.businessName, row.customerName),
    businessId: row.businessId || '—',
    customerId: row.customerId || '—',
  };
};

const resolveSourceRecord = (row = {}, type, scenario) => {
  if (type === 'Post') return scenario?.post?.postId || row.postId || row.sourceRecordId || '—';
  if (type === 'Review') return scenario?.review?.reviewId || row.reviewId || '—';
  if (type === 'Business Reply') {
    return scenario?.review?.reply?.replyId || row.replyId || '—';
  }
  if (type === 'Message') {
    return scenario?.conversation?.conversationId || row.conversationId || '—';
  }
  if (type === 'Quote') return scenario?.quote?.quoteId || row.quoteId || '—';
  if (type === 'Business Profile Item') {
    return scenario?.business?.businessId || row.businessId || '—';
  }
  return row.sourceRecordId || '—';
};

const resolveFlaggedItemRefs = (row = {}, type, scenario) => {
  if (type === 'Message') {
    const ids = (scenario?.messages || []).filter((m) => m.isFlagged).map((m) => m.id);
    return ids.length ? ids.join(', ') : scenario?.flag?.messageThreadId || '—';
  }
  if (type === 'Quote') {
    const ids = (scenario?.chatMessages || []).filter((m) => m.isFlagged).map((m) => m.id);
    return ids.length ? ids.join(', ') : scenario?.flag?.quoteThreadId || '—';
  }
  if (type === 'Post') {
    return scenario?.post?.postId || row.postId || '—';
  }
  if (type === 'Review') return scenario?.review?.reviewId || row.reviewId || '—';
  if (type === 'Business Reply') return scenario?.review?.reply?.replyId || '—';
  if (type === 'Business Profile Item') {
    return scenario?.profileContent?.description
      ? 'Business profile description'
      : scenario?.business?.businessId || '—';
  }
  return '—';
};

export const resolveContentControlRule = (type) =>
  CONTENT_CONTROL_RULE_BY_TYPE[type] || { id: '—', issue: '—' };

export const findScenarioForCase = (caseId) =>
  getScenarioByCaseId(caseId) ||
  getReviewScenarioByCaseId(caseId) ||
  getMessageScenarioByCaseId(caseId) ||
  getQuoteScenarioByCaseId(caseId) ||
  getProfileScenarioByCaseId(caseId);

export const normalizeQueueCase = (row = {}, scenario, { includeDecided = false } = {}) => {
  const type = normalizeQueueType(row, scenario);
  if (!type) return null;

  let status = normalizeQueueStatus(row.status || scenario?.case?.status);
  if (!isQueueStatus(status)) return null;

  const source = normalizeQueueSource(row, scenario);
  const account = resolveAccount(row, type, scenario);
  const supportTeam = normalizeSupportTeam(row.assignedTo ?? scenario?.case?.assignedTo);
  // New = unassigned. Under Review requires a Support Team member.
  if (supportTeam !== 'Unassigned' && status === 'New') {
    status = 'Under Review';
  } else if (supportTeam === 'Unassigned' && status === 'Under Review') {
    status = 'New';
  }
  const caseReason = dash(
    row.flagReason ||
      row.reviewReason ||
      row.reason ||
      scenario?.flag?.reason ||
      scenario?.flag?.trigger
  );
  const severity = dash(row.severity || row.priority || scenario?.case?.severity || scenario?.flag?.severity);
  const sourceRecordId = resolveSourceRecord(row, type, scenario);
  const flagId = dash(row.flagId || scenario?.flag?.flagId);
  const caseId = dash(row.caseId || scenario?.case?.caseId || row.id);

  return {
    ...row,
    caseId,
    moderationCaseId: caseId,
    flagId,
    source,
    queueSource: source,
    type,
    queueType: type,
    contentType: type,
    caseReason,
    flagReason: caseReason,
    reviewReason: caseReason,
    severity,
    accountName: account.accountName,
    accountKind: account.accountKind,
    accountType: account.accountKind,
    businessId: account.businessId,
    customerId: account.customerId,
    supportTeam,
    assignedTo: supportTeam,
    status,
    lastUpdated: row.updated || scenario?.case?.updated || row.lastUpdated || row.created,
    received: row.created || scenario?.case?.created || row.received,
    created: row.created || scenario?.case?.created,
    updated: row.updated || scenario?.case?.updated,
    sourceRecordId,
    flaggedItemRefs: resolveFlaggedItemRefs(row, type, scenario),
    snapshotRef: flagId !== '—' ? `SNAP-${String(flagId).replace(/\D/g, '') || '000000'}` : '—',
    postTitle: scenario?.post?.title || row.postTitle || row.preview || '—',
    quoteTitle: scenario?.quote?.title || row.preview || '—',
    businessName: scenario?.business?.businessName || row.businessName || account.accountName,
  };
};

export const collectQueueCases = () => {
  const raw = [
    ...getQueueCases(),
    ...getReviewQueueCases(),
    ...getMessageQueueCases(),
    ...getQuoteQueueCases(),
    ...getProfileQueueCases().filter((row) => row.profileType === 'Business'),
  ];

  const archivedIds = getArchivedModerationCaseIdSet();
  const seen = new Set();
  return raw
    .map((row) => normalizeQueueCase(row, findScenarioForCase(row.caseId)))
    .map((row) => applyQueueSession(row))
    .filter((row) => {
      if (!row || !row.caseId || row.caseId === '—') return false;
      const digits = stripModerationCasePrefix(row.caseId);
      if (digits && archivedIds.has(digits)) return false;
      if (seen.has(row.caseId)) return false;
      seen.add(row.caseId);
      return true;
    });
};

export const matchesQueueSearch = (row, query) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    row.caseId,
    row.moderationCaseId,
    row.flagId,
    row.sourceFlagId,
    row.accountName,
    row.sourceRecordId,
    row.supportTeam,
    row.assignedTo,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
};

export const matchesQueueFilters = (row, filters = {}) => {
  if (filters.status && filters.status !== 'All' && row.status !== filters.status) {
    return false;
  }
  if (filters.severity && filters.severity !== 'All') {
    if (String(row.severity || '').trim() !== filters.severity) return false;
  }
  if (filters.supportTeam && filters.supportTeam !== 'All') {
    const member = normalizeSupportTeam(row.supportTeam || row.assignedTo);
    if (filters.supportTeam === 'Unassigned') {
      if (member && member !== 'Unassigned' && member !== '—') return false;
    } else if (member !== filters.supportTeam) {
      return false;
    }
  }
  return true;
};

export const getSupportTeamFilterOptions = (rows = []) => {
  const names = [
    ...new Set(
      rows
        .map((row) => normalizeSupportTeam(row.supportTeam || row.assignedTo))
        .filter((name) => name && name !== 'Unassigned' && name !== '—')
    ),
  ].sort((a, b) => a.localeCompare(b));
  return [
    { value: 'All', label: 'All' },
    { value: 'Unassigned', label: 'Unassigned' },
    ...names.map((name) => ({ value: name, label: name })),
  ];
};

export const getCaseStatusFilterOptions = () => [
  { value: 'All', label: 'All' },
  { value: 'New', label: 'New' },
  { value: 'Under Review', label: 'Under Review' },
  { value: 'Decided', label: 'Decided' },
];

export const getSeverityFilterOptions = () => [
  { value: 'All', label: 'All' },
  ...QUEUE_SEVERITIES.map((severity) => ({ value: severity, label: severity })),
];

export const getTypeFilterOptions = () => [
  { value: 'All', label: 'All' },
  ...QUEUE_CASE_TYPES.map((type) => ({ value: type, label: type })),
];

export const getSourceFilterOptions = () => [
  { value: 'All', label: 'All' },
  ...QUEUE_SOURCES.map((source) => ({ value: source, label: source })),
];

export const deriveDecisionOutcomeFromFindings = (findings = []) => {
  const list = (findings || []).map((item) => String(item.finding || '').trim());
  if (!list.length) return null;
  if (list.some((finding) => !finding || finding === 'Unable to Determine')) return null;
  if (list.every((finding) => finding === 'Not Confirmed')) return 'Not Confirmed';
  if (list.some((finding) => finding === 'Confirmed') && list.every((finding) => finding === 'Confirmed' || finding === 'Not Confirmed')) {
    return 'Confirmed';
  }
  return null;
};

export const resolveCaseHeader = (row = {}, scenario) => {
  const type = row.queueType || row.type || normalizeQueueType(row, scenario);
  if (type === 'Post') return scenario?.post?.title || row.postTitle || row.preview || 'Post';
  if (type === 'Review') return 'Review';
  if (type === 'Business Reply') return 'Business Reply';
  if (type === 'Message') return 'Message';
  if (type === 'Quote') return scenario?.quote?.title || row.quoteTitle || row.preview || 'Quote';
  if (type === 'Business Profile Item') {
    return scenario?.business?.businessName || row.businessName || row.accountName || 'Business Profile Item';
  }
  return 'Moderation Case';
};

export const findModerationDecisionForm = (forms = buildSampleFormInventory()) =>
  (forms || []).find(
    (form) =>
      form.id === MODERATION_DECISION_FORM_ID ||
      form.formKey === 'moderation_decision_form' ||
      form.implementedFormKey === 'moderation_decision_form' ||
      String(form.formName || '').trim() === MODERATION_DECISION_FORM_NAME
  ) || null;

export const getModerationDecisionChecklist = (forms = buildSampleFormInventory()) => {
  const form = findModerationDecisionForm(forms);
  const fields = (form?.fields || []).filter((field) => field.controlType === 'Checkbox');
  if (fields.length) {
    return fields.map((field) => ({
      id: field.id,
      label: field.fieldLabel,
      mandatory: true,
    }));
  }
  return DEFAULT_DECISION_CHECK_LABELS.map((label, index) => ({
    id: `MDC-${String(index + 1).padStart(3, '0')}`,
    label,
    mandatory: true,
  }));
};

export const deriveOverallResult = (findings = []) => {
  if (!findings.length) return null;
  if (findings.some((item) => !item.finding)) return null;
  if (findings.some((item) => item.finding === 'Request More Information' || item.finding === 'Unable to Determine')) {
    return null;
  }
  if (findings.every((item) => item.finding === 'Not Approved' || item.finding === 'Not Confirmed')) {
    return 'Not Approved';
  }
  if (findings.some((item) => item.finding === 'Transfer')) return 'Transfer';
  if (findings.some((item) => item.finding === 'Approved' || item.finding === 'Confirmed')) {
    return 'Approved';
  }
  return null;
};

export const buildFlaggedItems = (row = {}, scenario) => {
  const type = row.queueType || row.type;
  if (type === 'Message') {
    const flagged = (scenario?.messages || []).filter((m) => m.isFlagged);
    if (flagged.length) {
      return flagged.map((m) => ({
        id: m.id,
        label: `${m.id} — ${String(m.text || '').slice(0, 80)}`,
        finding: '',
      }));
    }
  }
  if (type === 'Quote') {
    const flagged = (scenario?.chatMessages || []).filter((m) => m.isFlagged);
    if (flagged.length) {
      return flagged.map((m) => ({
        id: m.id,
        label: `${m.id} — ${String(m.text || '').slice(0, 80)}`,
        finding: '',
      }));
    }
  }
  const ref = row.flaggedItemRefs && row.flaggedItemRefs !== '—' ? row.flaggedItemRefs : row.sourceRecordId;
  return [
    {
      id: ref || row.caseId || 'item-1',
      label: `${type || 'Item'} — ${ref || row.caseReason || 'Flagged item'}`,
      finding: '',
    },
  ];
};

export const buildCapturedEvidence = (row = {}, scenario) => {
  const type = row.queueType || row.type || '—';
  const sourceModule =
    type === 'Post'
      ? 'Posts'
      : type === 'Review' || type === 'Business Reply'
        ? 'Reviews'
        : type === 'Message'
          ? 'Messages'
          : type === 'Quote'
            ? 'Quotes'
            : type === 'Business Profile Item'
              ? 'Business Profiles'
              : '—';
  const contentFormat =
    type === 'Business Profile Item' && scenario?.profileContent?.profileImageUrl
      ? 'Image'
      : type === 'Post' && (scenario?.post?.images || []).length
        ? 'Image'
        : type === 'Review' && (scenario?.review?.attachments || []).length
          ? 'Image'
          : 'Text';

  return {
    type,
    sourceModule,
    sourceRecordId: dash(row.sourceRecordId),
    snapshotRef: dash(row.snapshotRef),
    capturedAt: formatQueueDateTime(row.created || scenario?.flag?.createdAt),
    flaggedItemRefs: dash(row.flaggedItemRefs),
    contentFormat,
  };
};

const displaySuppliedState = (value) => {
  const text = dash(value);
  if (text === '—') return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const buildSourceStateFields = (row = {}, scenario, { includeDecision = false } = {}) => {
  const currentSource =
    row.currentSourceStatus ||
    scenario?.post?.status ||
    scenario?.review?.status ||
    scenario?.quote?.status ||
    scenario?.profileContent?.status ||
    '';
  const fields = [
    { label: 'Original Input State', value: dash(row.originalInputState) },
    { label: 'Input State at Case Creation', value: dash(row.inputStateAtCreation) },
  ];
  if (includeDecision) {
    fields.push({ label: 'Input State at Decision', value: dash(row.inputStateAtDecision) });
  }
  fields.push(
    { label: 'Current Input State', value: dash(row.currentInputState) },
    { label: 'Source Status at Capture', value: dash(row.sourceStatusAtCapture) },
  );
  if (includeDecision) {
    fields.push({ label: 'Source Status at Decision', value: dash(row.sourceStatusAtDecision) });
  }
  fields.push({ label: 'Current Source Status', value: displaySuppliedState(currentSource) });
  return fields;
};

const blank = (value) => (value && value !== '—' ? value : null);

export const buildQueueOverviewModel = ({
  scenario,
  itemFromState,
  formatDateTime = formatQueueDateTime,
}) => {
  const item =
    normalizeQueueCase(itemFromState || {}, scenario, { includeDecided: true }) ||
    itemFromState ||
    {};
  const type = item.queueType || item.type || '—';
  const source = item.source || '—';
  const flag = scenario?.flag || {};
  const business = scenario?.business || scenario?.review?.business || {};
  const customer =
    scenario?.customer ||
    scenario?.review?.reviewer ||
    {};
  const ccRule = resolveContentControlRule(type);

  const accountKind = item.accountKind || (type === 'Review' || type === 'Message' || type === 'Quote'
    ? item.accountKind
    : 'Business');

  const businessAccount = {
    kind: 'Business',
    businessName: dash(business.businessName || business.name || item.businessName),
    businessId: dash(business.businessId || business.id || item.businessId),
    abn: dash(business.abn),
    accountHolderLegalName: dash(business.accountHolderLegalName || business.legalName),
    tradeCategory: dash(business.category || business.primaryTrade),
    accountType: 'Business',
    accountStatus: dash(
      business.accountStatus || business.status
        ? String(business.accountStatus || business.status).charAt(0).toUpperCase() +
          String(business.accountStatus || business.status).slice(1).toLowerCase()
        : 'Active'
    ),
  };

  const customerAccount = {
    kind: 'Customer',
    customerName: dash(customer.name || item.accountName),
    customerId: dash(customer.customerId || customer.id || item.customerId),
    accountType: 'Customer',
    accountStatus: dash(customer.accountStatus || customer.status || 'Active'),
    email: dash(customer.email),
    phone: dash(customer.mobile || customer.phone),
  };

  const flaggedBy =
    source === 'System'
      ? {
          kind: 'System',
          contentControlRuleId: ccRule.id,
          contentIssue: dash(flag.trigger || flag.reason || ccRule.issue || item.caseReason),
          matchClassification: 'Suspected',
          checkContext: dash(flag.category || 'Content Control'),
          flaggedOn: formatDateTime(flag.createdAt || item.created),
        }
      : source === 'Business'
        ? {
            kind: 'Business',
            businessName: dash(
              flag.flaggedBy ||
                business.businessName ||
                business.name ||
                item.businessName
            ),
            businessId: dash(business.businessId || business.id || item.businessId),
            flagReason: dash(flag.reason || flag.trigger || item.caseReason),
            additionalDetails: dash(scenario?.adminNote ? String(scenario.adminNote) : '—'),
            blockSelected: dash(flag.blockSelected),
            flaggedOn: formatDateTime(flag.createdAt || item.created),
          }
        : {
            kind: 'Customer',
            customerName: dash(
              flag.flaggedBy ||
                scenario?.reporter?.name ||
                customer.name ||
                item.accountName
            ),
            customerId: dash(customer.customerId || customer.id || item.customerId),
            flagReason: dash(flag.reason || flag.trigger || item.caseReason),
            additionalDetails: dash(scenario?.adminNote ? String(scenario.adminNote) : '—'),
            blockSelected: dash(flag.blockSelected),
            flaggedOn: formatDateTime(flag.createdAt || item.created),
          };

  const sourceRecordId = dash(item.sourceRecordId);
  const linked = {
    moderationDecisionId: blank(item.moderationDecisionId),
    previousModerationCaseId: null,
    relatedAppealId: null,
    relatedSupportTicketId: null,
    sourceFlagId: blank(item.flagId),
    sourceRecordId: blank(sourceRecordId),
    capturedSnapshotReference: blank(item.snapshotRef),
    exactFlaggedItemRefs: blank(item.flaggedItemRefs),
    warningRestrictionHistory: accountKind === 'Customer'
      ? blank(customerAccount.customerId)
      : blank(businessAccount.businessId),
    fraudSecurityCaseId: null,
    fraudSecurityDecisionId: null,
  };

  return {
    caseId: item.caseId,
    source,
    type,
    caseReason: dash(item.caseReason),
    severity: dash(item.severity),
    status: item.status || 'New',
    supportTeam: normalizeSupportTeam(item.supportTeam || item.assignedTo),
    received: formatDateTime(item.created || flag.createdAt),
    lastUpdated: formatDateTime(item.updated || item.lastUpdated),
    accountKind,
    businessAccount,
    customerAccount,
    flaggedBy,
    linked,
    evidence: buildCapturedEvidence(item, scenario),
  };
};

export const getLinkedRecordPath = (id, kind) => {
  if (!id || id === '—') return null;
  const value = String(id).trim();
  if (!value) return null;

  if (kind === 'flag' || value.startsWith('FLG-')) {
    const flagScenario = getScenarioByFlagId(value);
    const sourceId =
      flagScenario?.post?.postId ||
      flagScenario?.review?.reviewId ||
      flagScenario?.flag?.messageThreadId ||
      flagScenario?.quote?.quoteId ||
      flagScenario?.case?.sourceRecordId;
    if (sourceId) return getLinkedRecordPath(sourceId);
    return null;
  }

  if (value.startsWith('MOD-DEC-')) {
    const digits = value.replace(/\D/g, '').slice(-4);
    if (digits) {
      return `/admin/moderation/queue/${MODERATION_QUEUE_CASE_PREFIX}-${digits.padStart(6, '0')}`;
    }
    return null;
  }

  if (
    kind === 'case' ||
    value.startsWith(`${MODERATION_QUEUE_CASE_PREFIX}-`) ||
    value.startsWith(`${MODERATION_ARCHIVE_CASE_PREFIX}-`) ||
    /^MOD-\d/i.test(value)
  ) {
    return `/admin/moderation/queue/${encodeURIComponent(value)}`;
  }
  if (value.startsWith('APL-') || value.startsWith('APP-')) {
    return `/admin/appeals/${encodeURIComponent(value)}`;
  }
  if (value.startsWith('ST-') || value.startsWith('TKT-')) {
    return `/admin/support/tickets/${encodeURIComponent(value)}`;
  }
  if (value.startsWith('CCT-')) {
    const rule = buildSampleContentControlRules().find((item) => item.id === value);
    return rule?.ruleName
      ? `/admin/configuration/content-control/${urlNameSegment(rule.ruleName)}`
      : null;
  }
  if (value.startsWith('MRL-') || value.startsWith('MDR-')) return null;
  if (value.startsWith('FRD-') || value.startsWith('FSC-')) {
    return `/admin/fraud/alerts/${encodeURIComponent(value)}`;
  }
  if (value.startsWith('BUS-')) {
    return `/admin/users/businesses/${encodeURIComponent(value)}`;
  }
  if (value.startsWith('CUS-')) {
    return `/admin/users/customers/${encodeURIComponent(value)}`;
  }
  if (value.startsWith('POST-') || value.startsWith('PST-')) {
    return `/admin/posts/${encodeURIComponent(value)}`;
  }
  if (value.startsWith('RVW-') || value.startsWith('REV-')) {
    return `/admin/reviews/${encodeURIComponent(value)}`;
  }
  if (
    value.startsWith('MSG-') ||
    value.startsWith('MTH-') ||
    value.startsWith('QTE-') ||
    value.startsWith('QT-')
  ) {
    return `/admin/communication/all/${encodeURIComponent(value)}`;
  }
  return null;
};

export const decisionIdFromCaseId = (caseId) => {
  const digits = String(caseId || '').replace(/\D/g, '');
  return `MOD-DEC-${(digits || '0000').slice(-4)}`;
};
